import {
  DeleteCaseFilesRequest,
  DeleteCaseRequest,
  DeleteFirmDocumentsFolderRequest,
  DeleteFirmDocumentsRequest,
  DeleteIntakeFilesRequest,
  DeleteIntakeRequest,
  DeleteProviderFilesRequest,
  DeleteProviderRequest,
  DeleteTemplateFilesRequest,
  DeleteTemplateRequest,
  ExportCaseFilesToCsvRequest,
  ExportCasesToCsvRequest,
  ExportFirmDocumentsFoldersToCsvRequest,
  ExportFirmDocumentsToCsvRequest,
  ExportFlaggedPathsToCsvRequest,
  ExportIntakeFilesToCsvRequest,
  ExportIntakesToCsvRequest,
  ExportProviderFilesToCsvRequest,
  ExportProvidersToCsvRequest,
  ExportTemplateFilesToCsvRequest,
  ExportTemplatesToCsvRequest,
  JobRequest,
  SqlScriptJobRequest,
  transformJobRequest,
} from "../models/Jobs/JobRequest";
import axios from "axios";
import { Paging } from "../models/Paging";
import { JobType } from "../models/Jobs/JobType";
import { JobRequestStatus } from "../models/Jobs/JobRequestStatus";
import { JobEntityType } from "../models/Jobs/JobEntityType";
import useApi from "../services/ApiService";
import { GetCaseFilesForCaseQueryOptions } from "./CasesRepository";
import { GetFirmDocumentsForFirmDocumentsFolderQueryOptions } from "./FirmDocumentsFoldersRepository";
import { GetIntakeFilesForIntakeQueryOptions } from "./IntakesRepository";
import { GetProviderFilesForProviderQueryOptions } from "./ProvidersRepository";
import { GetTemplateFilesForTemplateQueryOptions } from "./TemplatesRepository";
import {
  BULK_DELETION_MAX_CSV_BYTES,
  BulkDeletionFamilyKey,
  BulkDeletionMode,
  bulkDeletionRoute,
  formatBytesAsMb,
} from "../models/Jobs/BulkDeletion";
import {
  SourceLinkOutcome,
  SourceLinkOwner,
  SourceLinkReason,
  SourceLinkState,
} from "../models/SourceLink";
import { MigrationType } from "../models/Statistics/MigrationType";

/** Mirrors StartSourceLinksExportToCsvRequest. Every field optional; omitting all exports the whole registry. */
export interface SourceLinksExportFilter {
  entityType?: MigrationType | null;
  outcome?: SourceLinkOutcome | null;
  /** The failure reason to narrow to, named apart from the operator's justification for exporting. */
  failureReason?: SourceLinkReason | null;
  state?: SourceLinkState | null;
  owner?: SourceLinkOwner | null;
  regressed?: boolean | null;
  failedBefore?: boolean | null;
  search?: string | null;
}

export class JobsRepository {
  private api = useApi();
  /** One page of the jobs the signed-in user can see; filtering, search and sorting happen on the server. */
  async getJobsPage(params: JobsPageParams): Promise<{ data: JobRequest[]; paging: Paging | null }> {
    try {
      const response = await this.api.get("/api/jobs", {
        params: {
          OrganizationId: params.organizationId ?? undefined,
          Type: params.type ?? undefined,
          Status: params.status ?? undefined,
          EntityType: params.entityType ?? undefined,
          Search: params.search || undefined,
          Sort: params.sort,
          Descending: params.descending,
          PageNumber: params.pageNumber,
          PageSize: params.pageSize,
        },
      });
      const paginationHeader = response.headers["x-pagination"];
      return {
        data: response.data.map((job: JobRequest) => transformJobRequest(job)),
        paging: paginationHeader ? JSON.parse(paginationHeader) : null,
      };
    } catch (error) {
      throw new Error(`Failed to get jobs: ${error}`);
    }
  }

  async getJobOrganizations(): Promise<JobOrganization[]> {
    try {
      const response = await this.api.get<JobOrganization[]>("/api/jobs/organizations");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to get job organizations: ${error}`);
    }
  }

  /** An organization's deletions from an uploaded id list, newest first. */
  async getBulkDeletionJobs(organizationId: string): Promise<JobRequest[]> {
    try {
      const response = await this.api.get(`/api/jobs/${organizationId}/bulk-deletions`);
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(`Failed to get uploaded list deletions: ${error}`);
    }
  }
  async deleteJob(jobId: string): Promise<void> {
    try {
      await this.api.delete(`/api/jobs/${jobId}`);
    } catch (error) {
      throw new Error(`Failed to abort job: ${error}`);
    }
  }

  /** Asks a running deletion to stop after the item in progress; the server ends it Stopped on its own. */
  async stopJob(jobId: string): Promise<void> {
    try {
      await this.api.post(`/api/jobs/${jobId}/stop`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        throw new Error("The job has already finished.");
      }
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        throw new Error(error.response.data?.errors?.JobId?.[0] ?? "This job cannot be stopped.");
      }
      throw new Error(`Failed to stop job: ${error}`);
    }
  }

  /**
   * Queues a deletion of everything named in an uploaded CSV of ids.
   *
   * One method for every family and both modes, because the request shape does not vary: the family and
   * whether the ids name files or entities are both carried by the route.
   */
  async startBulkDeletion(
    organizationId: string,
    family: BulkDeletionFamilyKey,
    mode: BulkDeletionMode,
    csv: File,
    reason: string,
    recordsOnly: boolean,
    onUploaded?: (percent: number) => void
  ): Promise<void> {
    const form = new FormData();
    form.append("csv", csv);
    form.append("reason", reason);
    form.append("recordsOnly", String(recordsOnly));
    try {
      await this.api.post(
        `/api/jobs/${organizationId}/${bulkDeletionRoute(family, mode)}`,
        form,
        {
          // No client timeout: accepting the upload also parses it and, for entity ids, hides everything it
          // names before returning. On a large list that outlasts the shared 30s timeout, which would abort
          // here and report a failure for a run the server has in fact started.
          timeout: 0,
          onUploadProgress: (event) => {
            const total = event.total ?? csv.size;
            if (onUploaded && total > 0) {
              onUploaded(Math.min(100, Math.round((event.loaded / total) * 100)));
            }
          },
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        throw new Error(
          "A deletion from an uploaded list is already running for this entity type. Wait for it to finish."
        );
      }
      // The proxy in front of the API rejects an oversized body itself, with an HTML page rather than the
      // problem details every other failure here carries.
      if (axios.isAxiosError(error) && error.response?.status === 413) {
        throw new Error(
          `That list is larger than the ${formatBytesAsMb(BULK_DELETION_MAX_CSV_BYTES)} one upload can carry. `
            + "Split it and run the parts one after another."
        );
      }
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const validationErrors: Record<string, string[]> | undefined = error.response?.data?.errors;
        if (validationErrors) {
          throw new Error(Object.values(validationErrors).flat().join("\n"));
        }
        const detail: string | undefined = error.response?.data?.detail;
        if (detail) {
          throw new Error(detail);
        }
      }
      throw new Error(`Failed to start the deletion: ${error}`);
    }
  }

  // Case methods
  async getCasesDeletionStatus(
    organizationId: string
  ): Promise<DeleteCaseRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/cases/delete/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch cases deletion status for organization with id: ${organizationId}`
      );
    }
  }
  async startCasesDeletion(
    organizationId: string,
    caseIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/cases/delete`,
        {
          caseIds: caseIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to start cases deletion with ids: ${caseIds}`);
    }
  }
  async getCaseFilesDeletionStatus(caseId?: string): Promise<DeleteCaseFilesRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/case/files/delete/status', {
        params: caseId 
          ? { caseId } 
          : undefined
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch case files deletion status`
      );
    }
  }
  async startCaseFilesDeletion(
    organizationId: string,
    caseId: string,
    fileIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/case/${caseId}/files/delete`,
        {
          fileIds: fileIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start case files deletion with ids: ${fileIds}`
      );
    }
  }
  async getCasesCsvGenerationStatus(
    organizationId: string
  ): Promise<ExportCasesToCsvRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/cases/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from cases status for organization with id: ${organizationId}`
      );
    }
  }
  async startCsvGenerationFromCases(
    organizationId: string,
    cases: { id: string; caseNumber: string }[],
    exportFilesData: boolean,
    exportReason: string | undefined
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/cases/csv`,
        {
          cases: cases,
          exportFilesData: exportFilesData,
          reason: exportReason
        }
      );
      return response.data;
    } catch (error) {
      const caseIds = cases.map((c) => c.id).join(", ");
      throw new Error(
        `Failed to start CSV generation from cases with ids: ${caseIds}`
      );
    }
  }
  async auditLogCasesCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/cases/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log cases csv export download")
    }
  }
  
  async getCaseFilesCsvGenerationStatus(organizationId: string, caseId?: string): Promise<ExportCaseFilesToCsvRequest[]> {
    try {
      const response = await this.api.get(`/api/jobs/case/files/csv/status`, {
        params: {
          organizationId,
          ...(caseId ? { caseId } : {})
        }
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from case files status`
      );
    }
  }
  
  async startCsvGenerationFromCaseFiles(
    organizationId: string,
    options: GetCaseFilesForCaseQueryOptions = {},
    exportReason: string | undefined,
    caseId?: string
  ): Promise<void> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      nestedFolder1 = null,
      nestedFolder2 = null,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null,
    } = options;
    try {
      const response = await this.api.post(
        `/api/jobs/case/files/csv`,
        {
          organizationId: organizationId,
          migrationEntityId: caseId,
          reason: exportReason
        },
        {
          params: {
            ConflictReason: conflictReason,
            ConflictStatus: conflictStatus,
            Status: status,
            Extension: extension,
            NestedFolder1: nestedFolder1,
            NestedFolder2: nestedFolder2,
            FilePath: filePath,
            DestinationPath: destinationPath,
            MinFileSize: minFileSize,
            MaxFileSize: maxFileSize,
            PageNumber: pageNumber,
            PageSize: pageSize,
            OrderBy: orderBy,
            Descending: descending,
            Type: type,
            FailureType: failureType,
          },
          paramsSerializer: {
            indexes: true,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start CSV generation from case files caseId: ${caseId}`
      );
    }
  }
  
  async auditLogCaseFilesViewCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/case/files/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log case files view csv export download")
    }
  }

  // Intake methods
  async getIntakesDeletionStatus(
    organizationId: string
  ): Promise<DeleteIntakeRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/intakes/delete/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to fetch intakes deletion status for organization with id: ${organizationId}`
      );
    }
  }
  
  async startIntakesDeletion(
    organizationId: string,
    intakeIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/intakes/delete`,
        {
          intakeIds: intakeIds,
        }
      );
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to start intakes deletion with ids: ${intakeIds}`
      );
    }
  }

  async getIntakeCsvGenerationStatus(
    organizationId: string
  ): Promise<ExportIntakesToCsvRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/intakes/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to fetch csv generation from intake status for organization with id: ${organizationId}`
      );
    }
  }

  async getIntakeFilesCsvGenerationStatus(organizationId: string, intakeId?: string): Promise<ExportIntakeFilesToCsvRequest[]> {
    try {
      const response = await this.api.get(`/api/jobs/intake/files/csv/status`, {
        params: {
          organizationId,
          ...(intakeId ? { intakeId } : {})
        }
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from intake files status.`
      );
    }
  }

  async startCsvGenerationFromIntakes(
    organizationId: string,
    intakes: { id: string; intakeNumber: string }[],
    exportFilesData: boolean,
    exportReason: string | undefined
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/intakes/csv`,
        {
          intakes: intakes,
          exportFilesData: exportFilesData,
          reason: exportReason
        }
      );
      return response.data;
    } catch (error) {
      const intakeIds = intakes.map((i) => i.id).join(", ");
      throw new Error(
        `Failed to start csv generation from intake with ids: ${intakeIds}`
      );
    }
  }

  async auditLogIntakesCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/intakes/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log intakes csv export download")
    }
  }
  
  async startCsvGenerationFromIntakeFiles(
    organizationId: string,
    options: GetIntakeFilesForIntakeQueryOptions = {},
    exportReason: string | undefined,
    intakeId?: string,
  ): Promise<void> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      nestedFolder1 = null,
      nestedFolder2 = null,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null,
    } = options;
    try {
      const response = await this.api.post(
        `/api/jobs/intake/files/csv`,
        {
          organizationId: organizationId,
          migrationEntityId: intakeId,
          reason: exportReason
        },
        {
          params: {
            ConflictReason: conflictReason,
            ConflictStatus: conflictStatus,
            Status: status,
            Extension: extension,
            NestedFolder1: nestedFolder1,
            NestedFolder2: nestedFolder2,
            FilePath: filePath,
            DestinationPath: destinationPath,
            MinFileSize: minFileSize,
            MaxFileSize: maxFileSize,
            PageNumber: pageNumber,
            PageSize: pageSize,
            OrderBy: orderBy,
            Descending: descending,
            Type: type,
            FailureType: failureType,
          },
          paramsSerializer: {
            indexes: true,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start CSV generation for intake files`
      );
    }
  }

  async startIntakeFilesDeletion(
    organizationId: string,
    intakeId: string,
    fileIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/intake/${intakeId}/files/delete`,
        {
          fileIds: fileIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start intake files deletion with ids: ${fileIds}`
      );
    }
  }

  async getIntakeFilesDeletionStatus(intakeId?: string): Promise<DeleteIntakeFilesRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/intake/files/delete/status', {
        params: intakeId
          ? { intakeId }
          : undefined
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch intake files deletion status`
      );
    }
  }

  async auditLogIntakeFilesViewCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/intake/files/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log intake files view csv export download")
    }
  }

  // FirmDocumentsFolder methods
  async getFirmDocumentsFoldersDeletionStatus(
    organizationId: string
  ): Promise<DeleteFirmDocumentsFolderRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/firmDocumentsFolders/delete/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch firm documents folders deletion status for organization with id: ${organizationId}`
      );
    }
  }

  async startFirmDocumentsFoldersDeletion(
    organizationId: string,
    firmDocumentsFolderIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/firmDocumentsFolders/delete`,
        {
          firmDocumentsFolderIds: firmDocumentsFolderIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start firm documents folders deletion with ids: ${firmDocumentsFolderIds}`
      );
    }
  }

  async getFirmDocumentsFolderCsvGenerationStatus(
    organizationId: string
  ): Promise<ExportFirmDocumentsFoldersToCsvRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/firmDocumentsFolders/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from firm documents folder status for organization with id: ${organizationId}`
      );
    }
  }

  async startCsvGenerationFromFirmDocumentsFolders(
    organizationId: string,
    firmDocumentsFolders: { id: string; folderPath: string }[],
    exportFilesData: boolean,
    exportReason: string | undefined
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/firmDocumentsFolders/csv`,
        {
          firmDocumentsFolders: firmDocumentsFolders,
          exportFilesData: exportFilesData,
          reason: exportReason
        }
      );
      return response.data;
    } catch (error) {
      const firmDocumentsFolderIds = firmDocumentsFolders
        .map((i) => i.id)
        .join(", ");
      throw new Error(
        `Failed to start csv generation from firm documents folder with ids: ${firmDocumentsFolderIds}`
      );
    }
  }

  async auditLogFirmDocumentsFoldersCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/firmDocumentsFolders/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log firm documents folders csv export download")
    }
  }

  async startFirmDocumentsDeletion(
    organizationId: string,
    firmDocumentsFolderId: string,
    fileIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/firmDocumentsFolder/${firmDocumentsFolderId}/files/delete`,
        {
          fileIds: fileIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start firm documents folder files deletion with ids: ${fileIds}`
      );
    }
  }

  async getFirmDocumentsDeletionStatus(firmDocumentsFolderId?: string): Promise<DeleteFirmDocumentsRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/firmDocumentsFolder/files/delete/status', {
        params: firmDocumentsFolderId
          ? { firmDocumentsFolderId }
          : undefined
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch firm documents folder files deletion status`
      );
    }
  }

  async getFirmDocumentsCsvGenerationStatus(organizationId: string, firmDocumentsFolderId?: string): Promise<ExportFirmDocumentsToCsvRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/firmDocumentsFolder/files/csv/status', {
        params: {
          organizationId,
          ...(firmDocumentsFolderId ? { firmDocumentsFolderId } : {})
        }
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from firm documents folder files status`
      );
    }
  }

  async startCsvGenerationFromFirmDocuments(
    organizationId: string,
    options: GetFirmDocumentsForFirmDocumentsFolderQueryOptions = {},
    exportReason: string | undefined,
    firmDocumentsFolderId?: string,
  ): Promise<void> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null,
    } = options;
    try {
      const response = await this.api.post(
        `/api/jobs/firmDocumentsFolder/files/csv`,
        {
          organizationId: organizationId,
          migrationEntityId: firmDocumentsFolderId,
          reason: exportReason
        },
        {
          params: {
            ConflictReason: conflictReason,
            ConflictStatus: conflictStatus,
            Status: status,
            Extension: extension,
            FilePath: filePath,
            DestinationPath: destinationPath,
            MinFileSize: minFileSize,
            MaxFileSize: maxFileSize,
            PageNumber: pageNumber,
            PageSize: pageSize,
            OrderBy: orderBy,
            Descending: descending,
            Type: type,
            FailureType: failureType,
          },
          paramsSerializer: {
            indexes: true,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start CSV generation for firm documents.`
      );
    }
  }

  async auditLogFirmDocumentsViewCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/firmDocumentsFolder/files/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log intake files view csv export download")
    }
  }

  //Providers methods
  async getProvidersDeletionStatus(
    organizationId: string
  ): Promise<DeleteProviderRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/providers/delete/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch providers deletion status for organization with id: ${organizationId}`
      );
    }
  }
  async startProvidersDeletion(
    organizationId: string,
    providerIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/providers/delete`,
        {
          providerIds: providerIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start providers deletion with ids: ${providerIds}`
      );
    }
  }
  async getProviderFilesDeletionStatus(providerId?: string): Promise<DeleteProviderFilesRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/provider/files/delete/status', {
        params: providerId
          ? { providerId }
          : undefined
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch provider files deletion status`
      );
    }
  }
  async startProviderFilesDeletion(
    organizationId: string,
    providerId: string,
    fileIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/provider/${providerId}/files/delete`,
        {
          fileIds: fileIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start provider files deletion with ids: ${fileIds}`
      );
    }
  }
  async getProvidersCsvGenerationStatus(
    organizationId: string
  ): Promise<ExportProvidersToCsvRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/providers/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from providers status for organization with id: ${organizationId}`
      );
    }
  }
  async startCsvGenerationFromProviders(
    organizationId: string,
    providers: { id: string; providerNameId: string }[],
    exportFilesData: boolean,
    exportReason: string | undefined
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/providers/csv`,
        {
          providers: providers,
          exportFilesData: exportFilesData,
          reason: exportReason,
        }
      );
      return response.data;
    } catch (error) {
      const providerIds = providers.map((p) => p.id).join(", ");
      throw new Error(
        `Failed to start CSV generation from providers with ids: ${providerIds}`
      );
    }
  }
  async auditLogProvidersCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/providers/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log providers csv export download")
    }
  }
  async getProviderFilesCsvGenerationStatus(organizationId: string, providerId?: string): Promise<ExportProviderFilesToCsvRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/provider/files/csv/status', {
        params: {
          organizationId,
          ...(providerId ? { providerId } : {})
        }
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from provider files`
      );
    }
  }
  async startCsvGenerationFromProviderFiles(
    organizationId: string,
    options: GetProviderFilesForProviderQueryOptions = {},
    exportReason: string | undefined,
    providerId?: string,
  ): Promise<void> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null,
    } = options;
    try {
      const response = await this.api.post(
        `/api/jobs/provider/files/csv`,
        {
          organizationId: organizationId,
          migrationEntityId: providerId,
          reason: exportReason
        },
        {
          params: {
            ConflictReason: conflictReason,
            ConflictStatus: conflictStatus,
            Status: status,
            Extension: extension,
            FilePath: filePath,
            DestinationPath: destinationPath,
            MinFileSize: minFileSize,
            MaxFileSize: maxFileSize,
            PageNumber: pageNumber,
            PageSize: pageSize,
            OrderBy: orderBy,
            Descending: descending,
            Type: type,
            FailureType: failureType,
          },
          paramsSerializer: {
            indexes: true,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start CSV generation from provider files providerId: ${providerId}`
      );
    }
  }

  async auditLogProviderFilesViewCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/provider/files/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log provider files view csv export download")
    }
  }

  //Templates methods
  async getTemplatesDeletionStatus(
    organizationId: string
  ): Promise<DeleteTemplateRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/templates/delete/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch templates deletion status for organization with id: ${organizationId}`
      );
    }
  }
  async startTemplatesDeletion(
    organizationId: string,
    templateIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/templates/delete`,
        {
          templateIds: templateIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start templates deletion with ids: ${templateIds}`
      );
    }
  }
  async getTemplateFilesDeletionStatus(templateId?: string): Promise<DeleteTemplateFilesRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/template/files/delete/status', {
        params: templateId
          ? { templateId }
          : undefined
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch template files deletion status`
      );
    }
  }
  
  async startTemplateFilesDeletion(
    organizationId: string,
    templateId: string,
    fileIds: string[]
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/template/${templateId}/files/delete`,
        {
          fileIds: fileIds,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start template files deletion with ids: ${fileIds}`
      );
    }
  }
  async getTemplatesCsvGenerationStatus(
    organizationId: string
  ): Promise<ExportTemplatesToCsvRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/templates/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from templates status for organization with id: ${organizationId}`
      );
    }
  }
  async startCsvGenerationFromTemplates(
    organizationId: string,
    templates: { id: string; templateType: string }[],
    exportFilesData: boolean,
    exportReason: string | undefined
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/templates/csv`,
        {
          templates: templates,
          exportFilesData: exportFilesData,
          reason: exportReason
        }
      );
      return response.data;
    } catch (error) {
      const templateIds = templates.map((p) => p.id).join(", ");
      throw new Error(
        `Failed to start CSV generation from templates with ids: ${templateIds}`
      );
    }
  }
  async auditLogTemplatesCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/templates/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log templates csv export download")
    }
  }
  
  async getTemplateFilesCsvGenerationStatus(organizationId: string, templateId?: string): Promise<ExportTemplateFilesToCsvRequest[]> {
    try {
      const response = await this.api.get('/api/jobs/template/files/csv/status', {
        params: {
          organizationId,
          ...(templateId ? { templateId } : {})
        }
      });
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch csv generation from template files status`
      );
    }
  }
  async startCsvGenerationFromTemplateFiles(
    organizationId: string,
    options: GetTemplateFilesForTemplateQueryOptions = {},
    exportReason: string | undefined,
    templateId?: string
  ): Promise<void> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null,
    } = options;
    try {
      const response = await this.api.post(
        `/api/jobs/template/files/csv`,
        {
          organizationId: organizationId,
          migrationEntityId: templateId,
          reason: exportReason
        },
        {
          params: {
            ConflictReason: conflictReason,
            ConflictStatus: conflictStatus,
            Status: status,
            Extension: extension,
            FilePath: filePath,
            DestinationPath: destinationPath,
            MinFileSize: minFileSize,
            MaxFileSize: maxFileSize,
            PageNumber: pageNumber,
            PageSize: pageSize,
            OrderBy: orderBy,
            Descending: descending,
            Type: type,
            FailureType: failureType,
          },
          paramsSerializer: {
            indexes: true,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start CSV generation for template files`
      );
    }
  }

  async auditLogTemplateFilesViewCsvExportDownload(
    exportJobId: string,
    reason?: string
  ) : Promise<void> {
    try {
      await this.api.post(`/api/jobs/template/files/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason
      })
    } catch (error) {
      throw new Error("Failed to audit log template files view csv export download")
    }
    }

  async getSqlScriptExecutionStatus(organizationId: string): Promise<SqlScriptJobRequest[]> {
    try {
        const response = await this.api.get<SqlScriptJobRequest[]>(`/api/jobs/${organizationId}/sqlScripts/status`);
        return response.data.map(job => Object.assign(new SqlScriptJobRequest(), job));
    } catch (error) {
        throw new Error(`Failed to fetch SQL script execution status: ${error}`);
    }
  }

  async executeSqlScript(organizationId: string, sqlScript: string, reason: string, followUpRecalculationTypes: MigrationType[] = []): Promise<void> {
    try {
        await this.api.post(`/api/jobs/${organizationId}/sqlScripts/execute`, { sqlScript, reason, followUpRecalculationTypes });
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
            const validationErrors: Record<string, string[]> | undefined = error.response?.data?.errors;
            if (validationErrors) {
                const messages = Object.values(validationErrors).flat();
                throw new Error(messages.join("\n"));
            }
            const detail: string | undefined = error.response?.data?.detail;
            if (detail) {
                throw new Error(detail);
            }
        }
        throw new Error(`Failed to submit SQL script for execution: ${error}`);
    }
  }

  // FlaggedPaths methods
  async getFlaggedPathCsvGenerationStatus(
    organizationId: string
  ): Promise<ExportFlaggedPathsToCsvRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/flaggedPaths/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch flagged paths CSV export status for organization: ${organizationId}`
      );
    }
  }

  async startCsvGenerationFromFlaggedPaths(
    organizationId: string,
    flaggedPaths: { id: string; path: string }[],
    reason: string | undefined
  ): Promise<void> {
    try {
      const response = await this.api.post(
        `/api/jobs/${organizationId}/flaggedPaths/csv`,
        {
          reason,
          flaggedPaths: flaggedPaths.map((flaggedPath) => ({
            id: flaggedPath.id,
            path: flaggedPath.path,
          })),
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        `Failed to start flagged paths CSV export for organization: ${organizationId}`);
    }
  }

  async auditLogFlaggedPathsCsvExportDownload(
    exportJobId: string,
    reason?: string
  ): Promise<void> {
    try {
      await this.api.post(`/api/jobs/flaggedPaths/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason,
      });
    } catch (error) {
      throw new Error('Failed to audit log flagged paths CSV export download');
    }
  }

  async getSourceLinksCsvExportStatus(organizationId: string): Promise<JobRequest[]> {
    try {
      const response = await this.api.get(
        `/api/jobs/${organizationId}/sourceLinks/csv/status`
      );
      return response.data.map((job: JobRequest) => transformJobRequest(job));
    } catch (error) {
      throw new Error(
        `Failed to fetch source links CSV export status for organization: ${organizationId}`
      );
    }
  }

  /** Queues an export of the registry as currently filtered; see JobsController.StartSourceLinksCsvExport. */
  async startSourceLinksCsvExport(
    organizationId: string,
    filter: SourceLinksExportFilter,
    reason: string | undefined
  ): Promise<void> {
    try {
      await this.api.post(`/api/jobs/${organizationId}/sourceLinks/csv`, {
        reason,
        ...filter,
      });
    } catch (error) {
      throw new Error(
        `Failed to start source links CSV export for organization: ${organizationId}`
      );
    }
  }

  async auditLogSourceLinksCsvExportDownload(
    exportJobId: string,
    reason: string
  ): Promise<void> {
    try {
      await this.api.post(`/api/jobs/sourceLinks/csv/auditLogDownload`, {
        exportJobId: exportJobId,
        reason: reason,
      });
    } catch (error) {
      throw new Error('Failed to audit log source links CSV export download');
    }
  }
}

export type JobsSortKey = "status" | "organization" | "type" | "entityType" | "created";

export interface JobsPageParams {
  organizationId: string | null;
  type: JobType | null;
  status: JobRequestStatus | null;
  entityType: JobEntityType | null;
  search: string;
  sort: JobsSortKey;
  descending: boolean;
  pageNumber: number;
  pageSize: number;
}

export interface JobOrganization {
  id: string;
  name: string;
}

export default JobsRepository;
